import React, { useState, useEffect, useCallback, useRef } from 'react';
import './ApiDocs.css';

// ── Viewer embed ───────────────────────────────────────────────────────────────
function ViewerEmbed({ url, viewer }) {
  const src = `/scalar.html?url=${encodeURIComponent(url)}&viewer=${viewer}`;
  return (
    <iframe
      key={`${url}-${viewer}`}
      src={src}
      className="api-docs-frame"
      title="API Reference"
      sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
    />
  );
}

// ── Group combobox ─────────────────────────────────────────────────────────────
function GroupCombobox({ value, onChange, existingGroups, disabled }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Sync display value when dropdown closes
  useEffect(() => {
    if (!open) setSearch(value || '');
  }, [open, value]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered    = existingGroups.filter(g => g.toLowerCase().includes(search.toLowerCase()));
  const trimmed     = search.trim();
  const exactMatch  = existingGroups.some(g => g.toLowerCase() === trimmed.toLowerCase());
  const showCreate  = trimmed && !exactMatch;

  const select = (g) => { onChange(g || undefined); setOpen(false); };

  return (
    <div className="group-combobox" ref={ref}>
      <input
        className="api-modal-input group-combobox-input"
        placeholder="No group"
        value={open ? search : (value || '')}
        onFocus={() => { setOpen(true); setSearch(''); }}
        onChange={e => setSearch(e.target.value)}
        disabled={disabled}
        autoComplete="off"
      />
      <svg className="group-combobox-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M4 6l4 4 4-4"/>
      </svg>

      {open && (
        <div className="group-combobox-dropdown" onMouseDown={e => e.preventDefault()}>
          <div className="group-combobox-option group-combobox-none" onClick={() => select('')}>
            No group
          </div>

          {filtered.length > 0 && <div className="group-combobox-divider" />}

          {filtered.map(g => (
            <div
              key={g}
              className={`group-combobox-option ${g === value ? 'selected' : ''}`}
              onClick={() => select(g)}
            >
              {g}
              {g === value && (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M3 8l4 4 6-6"/>
                </svg>
              )}
            </div>
          ))}

          {showCreate && (
            <>
              {filtered.length > 0 && <div className="group-combobox-divider" />}
              <div className="group-combobox-option group-combobox-create" onClick={() => select(trimmed)}>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M8 2v12M2 8h12"/>
                </svg>
                Create "{trimmed}"
              </div>
            </>
          )}

          {!filtered.length && !showCreate && (
            <div className="group-combobox-empty">No groups yet</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Add spec modal ─────────────────────────────────────────────────────────────
function AddSpecModal({ onAdd, onClose, existingGroups, saving, error }) {
  const [label, setLabel] = useState('');
  const [yaml,  setYaml]  = useState('');
  const [group, setGroup] = useState(undefined);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!label.trim() || !yaml.trim()) return;
    onAdd({ label: label.trim(), yaml: yaml.trim(), group });
  };

  return (
    <div className="api-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="api-modal">
        <div className="api-modal-header">
          <h2 className="api-modal-title">New API Spec</h2>
          <button className="api-modal-close" onClick={onClose} disabled={saving}>✕</button>
        </div>

        <form className="api-modal-body" onSubmit={handleSubmit}>
          <label className="api-modal-label">Name</label>
          <input
            className="api-modal-input"
            placeholder="e.g. Payments API"
            value={label}
            onChange={e => setLabel(e.target.value)}
            autoFocus
            disabled={saving}
          />

          <label className="api-modal-label">
            Group <span className="api-modal-label-optional">(optional)</span>
          </label>
          <GroupCombobox
            value={group}
            onChange={setGroup}
            existingGroups={existingGroups}
            disabled={saving}
          />

          <label className="api-modal-label">OpenAPI YAML</label>
          <textarea
            className="api-modal-yaml"
            placeholder={"openapi: 3.0.0\ninfo:\n  title: My API\n  version: 1.0.0\npaths:\n  /example:\n    get:\n      summary: Example endpoint\n      responses:\n        '200':\n          description: OK"}
            value={yaml}
            onChange={e => setYaml(e.target.value)}
            disabled={saving}
            spellCheck={false}
          />

          {error && <div className="api-modal-error">{error}</div>}

          <div className="api-modal-actions">
            <button type="button" className="api-modal-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="submit"
              className="api-modal-save"
              disabled={!label.trim() || !yaml.trim() || saving}
            >
              {saving ? 'Uploading…' : 'Save & publish'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Delete confirmation modal ──────────────────────────────────────────────────
function DeleteConfirmModal({ spec, onConfirm, onClose, deleting }) {
  const [nameInput, setNameInput] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (nameInput === spec.label) onConfirm(spec);
  };

  return (
    <div className="api-modal-backdrop" onClick={e => { if (e.target === e.currentTarget && !deleting) onClose(); }}>
      <div className="api-modal api-modal-delete" onClick={e => e.stopPropagation()}>
        <div className="api-modal-header">
          <h2 className="api-modal-title">Delete API Spec</h2>
          <button className="api-modal-close" onClick={onClose} disabled={deleting} type="button">✕</button>
        </div>

        <form className="api-modal-body" onSubmit={handleSubmit}>
          <p className="api-delete-warning">
            This will permanently delete <strong>{spec.label}</strong> from storage.
            This action cannot be undone.
          </p>

          <label className="api-modal-label">Type the API name to confirm</label>
          <input
            className="api-modal-input"
            placeholder={spec.label}
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            autoFocus
            disabled={deleting}
          />

          <div className="api-modal-actions">
            <button type="button" className="api-modal-cancel" onClick={onClose} disabled={deleting}>Cancel</button>
            <button type="submit" className="api-modal-delete-btn" disabled={nameInput !== spec.label || deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Share modal ────────────────────────────────────────────────────────────────
function ShareModal({ spec, viewer, onClose }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/docs?share=${spec.shareId}&viewer=${viewer}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="api-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="api-modal api-modal-share" onClick={e => e.stopPropagation()}>
        <div className="api-modal-header">
          <h2 className="api-modal-title">Share API Docs</h2>
          <button className="api-modal-close" onClick={onClose} type="button">✕</button>
        </div>

        <div className="api-modal-body">
          <p className="api-share-hint">
            Anyone with this link can view the <strong>{spec.label}</strong> docs without logging in.
          </p>
          <label className="api-modal-label">Public link</label>
          <div className="api-share-url-row">
            <input
              className="api-modal-input api-share-url-input"
              readOnly
              value={url}
              onClick={e => e.target.select()}
            />
            <button className={`api-share-copy-btn ${copied ? 'copied' : ''}`} onClick={handleCopy} type="button" title={copied ? 'Copied!' : 'Copy link'}>
              {copied
                ? <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8l4 4 6-6"/></svg>
                : <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 9.5a3.5 3.5 0 005 0l2-2a3.5 3.5 0 00-5-5l-1 1"/><path d="M9.5 6.5a3.5 3.5 0 00-5 0l-2 2a3.5 3.5 0 005 5l1-1"/></svg>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Connect modal ──────────────────────────────────────────────────────────────
function ConnectModal({ apiBaseUrl, apiKey, onClose }) {
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedBody, setCopiedBody] = useState(false);

  const endpoint = `${apiBaseUrl}/api/specs${apiKey ? `?code=${apiKey}` : ''}`;
  const exampleBody = JSON.stringify({
    name: "My API",
    yaml: "openapi: 3.0.0\\ninfo:\\n  title: My API\\n  version: 1.0.0\\npaths:\\n  /example:\\n    get:\\n      summary: Example endpoint\\n      responses:\\n        '200':\\n          description: OK",
    group: "External APIs"
  }, null, 2);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(endpoint).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2500);
    });
  };

  const handleCopyBody = () => {
    navigator.clipboard.writeText(exampleBody).then(() => {
      setCopiedBody(true);
      setTimeout(() => setCopiedBody(false), 2500);
    });
  };

  return (
    <div className="api-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="api-modal api-modal-connect" onClick={e => e.stopPropagation()}>
        <div className="api-modal-header">
          <h2 className="api-modal-title">API Connection Info</h2>
          <button className="api-modal-close" onClick={onClose} type="button">✕</button>
        </div>

        <div className="api-modal-body">
          <p className="api-connect-hint">
            Use this endpoint to programmatically create or update API specs.
          </p>

          <label className="api-modal-label">Endpoint</label>
          <div className="api-connect-method-row">
            <span className="api-connect-method">POST</span>
            <div className="api-share-url-row">
              <input
                className="api-modal-input api-share-url-input"
                readOnly
                value={endpoint}
                onClick={e => e.target.select()}
              />
              <button className={`api-share-copy-btn ${copiedUrl ? 'copied' : ''}`} onClick={handleCopyUrl} type="button" title={copiedUrl ? 'Copied!' : 'Copy link'}>
                {copiedUrl
                  ? <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8l4 4 6-6"/></svg>
                  : <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 9.5a3.5 3.5 0 005 0l2-2a3.5 3.5 0 00-5-5l-1 1"/><path d="M9.5 6.5a3.5 3.5 0 00-5 0l-2 2a3.5 3.5 0 005 5l1-1"/></svg>
                }
              </button>
            </div>
          </div>

          <label className="api-modal-label">Headers</label>
          <div className="api-connect-code-block">
            <code>Content-Type: application/json</code>
          </div>

          <div className="api-connect-row-with-copy">
            <label className="api-modal-label">Request Body</label>
            <button className={`api-connect-copy-code-btn ${copiedBody ? 'copied' : ''}`} onClick={handleCopyBody} type="button">
              {copiedBody ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <div className="api-connect-code-block">
            <pre>{exampleBody}</pre>
          </div>

          <div className="api-connect-info">
            <svg viewBox="0 0 16 16" fill="currentColor">
              <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 7v4M8 4.5v.5"/>
            </svg>
            <div>
              <strong>Create or Update:</strong> If a spec with the same name exists, it will be updated and the share ID will be preserved. Otherwise, a new spec is created.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Spec item ──────────────────────────────────────────────────────────────────
function SpecItem({ spec, isActive, viewer, onSelect, onRemove, removing }) {
  const [showShare,  setShowShare]  = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  return (
    <>
      <div className={`api-spec-item ${isActive ? 'active' : ''}`} onClick={() => onSelect(spec)}>
        <span className="api-spec-label">{spec.label}</span>

        <div className="api-spec-actions">
          <button
            className="api-spec-action-btn share"
            onClick={e => { e.stopPropagation(); setShowShare(true); }}
            type="button"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 2l3 3-3 3M14 5H6a4 4 0 000 8h1"/>
            </svg>
            Share
          </button>

          <button
            className="api-spec-action-btn delete"
            onClick={e => { e.stopPropagation(); setShowDelete(true); }}
            type="button"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 9h8l1-9"/>
            </svg>
            Delete
          </button>
        </div>
      </div>

      {showShare && spec.shareId && (
        <ShareModal spec={spec} viewer={viewer} onClose={() => setShowShare(false)} />
      )}
      {showDelete && (
        <DeleteConfirmModal
          spec={spec}
          onConfirm={onRemove}
          onClose={() => setShowDelete(false)}
          deleting={removing === spec.id}
        />
      )}
    </>
  );
}

// ── Group section ──────────────────────────────────────────────────────────────
function GroupSection({ name, specs, isOpen, onToggle, isActive, viewer, onSelect, onRemove, removing }) {
  return (
    <div className="api-spec-group">
      <button className="api-spec-group-header" onClick={onToggle} type="button">
        <svg
          className={`api-spec-group-chevron ${isOpen ? 'open' : ''}`}
          viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
        >
          <path d="M4 6l4 4 4-4"/>
        </svg>
        <span className="api-spec-group-name">{name}</span>
        <span className="api-spec-group-count">{specs.length}</span>
      </button>

      {isOpen && specs.map(spec => (
        <SpecItem
          key={spec.id}
          spec={spec}
          isActive={isActive(spec)}
          viewer={viewer}
          onSelect={onSelect}
          onRemove={onRemove}
          removing={removing}
        />
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
function ApiDocs({ apiCall, apiBaseUrl, apiKey, onClose }) {
  const [specs,       setSpecs]       = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [adding,      setAdding]      = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [addError,    setAddError]    = useState(null);
  const [removing,    setRemoving]    = useState(null);
  const [viewer,      setViewer]      = useState('swagger');
  const [search,      setSearch]      = useState('');
  const [collapsed,   setCollapsed]   = useState({});

  const specUrl = useCallback((id) => {
    const sep = apiBaseUrl.includes('?') ? '&' : '?';
    return `${apiBaseUrl}/api/specs/${id}${apiKey ? `${sep}code=${apiKey}` : ''}`;
  }, [apiBaseUrl, apiKey]);

  useEffect(() => {
    apiCall('specs', 'GET')
      .then(data => {
        const loaded = data.map(s => ({ ...s, url: specUrl(s.id) }));
        setSpecs(loaded);
        setSelected(loaded[0] || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [apiCall, specUrl]);

  const handleAdd = async ({ label, yaml, group }) => {
    setSaving(true);
    setAddError(null);
    try {
      const record = await apiCall('specs', 'POST', { name: label, yaml, group });
      const spec   = { ...record, url: specUrl(record.id) };
      setSpecs(prev => {
        const exists = prev.some(s => s.id === spec.id);
        const next = exists ? prev.map(s => s.id === spec.id ? spec : s) : [...prev, spec];
        return next.sort((a, b) => {
          const ga = a.group ?? '\xFF', gb = b.group ?? '\xFF';
          return ga !== gb ? ga.localeCompare(gb) : a.label.localeCompare(b.label);
        });
      });
      setSelected(spec);
      setAdding(false);
    } catch (err) {
      setAddError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (spec) => {
    setRemoving(spec.id);
    try { await apiCall(`specs/${spec.id}`, 'DELETE'); } catch { /* ignore */ }
    setSpecs(prev => {
      const next = prev.filter(s => s.id !== spec.id);
      if (selected?.id === spec.id) setSelected(next[0] || null);
      return next;
    });
    setRemoving(null);
  };

  const toggleGroup = (g) => setCollapsed(prev => ({ ...prev, [g]: !prev[g] }));

  const isSearching  = search.trim().length > 0;
  const existingGroups = [...new Set(specs.filter(s => s.group).map(s => s.group))].sort();

  const filtered = specs.filter(s =>
    s.label.toLowerCase().includes(search.toLowerCase()) ||
    s.id.toLowerCase().includes(search.toLowerCase())
  );

  const ungrouped  = filtered.filter(s => !s.group);
  const groupNames = [...new Set(filtered.filter(s => s.group).map(s => s.group))].sort();
  const hasGroups  = existingGroups.length > 0;

  const isGroupOpen = (g) => isSearching || !collapsed[g];

  const renderSpec = (spec) => (
    <SpecItem
      key={spec.id}
      spec={spec}
      isActive={selected?.id === spec.id}
      viewer={viewer}
      onSelect={setSelected}
      onRemove={handleRemove}
      removing={removing}
    />
  );

  return (
    <div className="api-docs-overlay">
      <aside className="api-docs-sidebar">
        {/* Logo */}
        <div className="api-docs-logo-container">
          <button className="api-docs-logo-back-btn" onClick={onClose} title="Back to dashboard">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 12L6 8l4-4"/>
            </svg>
          </button>
          <img
            src="https://cdn.prod.website-files.com/67e26aef79a7047719ea9db8/67e28ceff1544e195a3a2269_astena.svg"
            alt="Astena Logo"
            className="api-docs-logo"
          />
        </div>

        {/* Header */}
        <div className="api-docs-sidebar-header">
          <h1 className="api-docs-title">API Reference</h1>
          <button className="api-docs-connect-btn" onClick={() => setShowConnect(true)} title="API Connection Info">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="8" cy="8" r="6"/>
              <path d="M8 7v4"/>
              <circle cx="8" cy="4.5" r="0.5" fill="currentColor" stroke="none"/>
            </svg>
          </button>
          <button className="api-docs-add-btn" onClick={() => { setAdding(true); setAddError(null); }} title="Add API">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M8 2v12M2 8h12"/>
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="api-docs-search-wrap">
          <svg className="api-docs-search-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <circle cx="6.5" cy="6.5" r="4"/>
            <path d="M11 11l3 3"/>
          </svg>
          <input
            className="api-docs-search"
            placeholder="Search APIs…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="api-docs-search-clear" onClick={() => setSearch('')}>✕</button>}
        </div>

        {/* Viewer toggle */}
        <div className="api-docs-viewer-toggle">
          <button className={`api-docs-viewer-btn ${viewer === 'swagger' ? 'active' : ''}`} onClick={() => setViewer('swagger')}>
            Swagger UI
          </button>
          <button className={`api-docs-viewer-btn ${viewer === 'scalar' ? 'active' : ''}`} onClick={() => setViewer('scalar')}>
            Scalar
          </button>
        </div>

        {/* Spec list */}
        <div className="api-docs-spec-list">
          {loading && <div className="api-docs-empty"><p>Loading…</p></div>}

          {!loading && filtered.length === 0 && (
            <div className="api-docs-empty">
              {specs.length === 0 ? (
                <>
                  <p>No APIs added yet.</p>
                  <button className="api-docs-add-first" onClick={() => setAdding(true)}>Add your first API</button>
                </>
              ) : (
                <p>No results for "{search}"</p>
              )}
            </div>
          )}

          {/* Ungrouped — labelled "Other" only when groups also exist */}
          {!loading && ungrouped.length > 0 && (
            <div className={hasGroups ? 'api-spec-group' : ''}>
              {hasGroups && (
                <button className="api-spec-group-header" onClick={() => toggleGroup('__ungrouped__')} type="button">
                  <svg
                    className={`api-spec-group-chevron ${isGroupOpen('__ungrouped__') ? 'open' : ''}`}
                    viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  >
                    <path d="M4 6l4 4 4-4"/>
                  </svg>
                  <span className="api-spec-group-name">Other</span>
                  <span className="api-spec-group-count">{ungrouped.length}</span>
                </button>
              )}
              {isGroupOpen('__ungrouped__') && ungrouped.map(renderSpec)}
            </div>
          )}

          {/* Grouped */}
          {!loading && groupNames.map(g => (
            <GroupSection
              key={g}
              name={g}
              specs={filtered.filter(s => s.group === g)}
              isOpen={isGroupOpen(g)}
              onToggle={() => toggleGroup(g)}
              isActive={spec => selected?.id === spec.id}
              viewer={viewer}
              onSelect={setSelected}
              onRemove={handleRemove}
              removing={removing}
            />
          ))}
        </div>
      </aside>

      {/* Viewer pane */}
      <div className="api-docs-viewer-pane">
        {selected ? (
          <ViewerEmbed url={selected.url} viewer={viewer} />
        ) : (
          !loading && (
            <div className="api-docs-placeholder">
              <div className="api-docs-placeholder-icon">⚙</div>
              <h3>No API selected</h3>
              <p>Add an API using the <strong>+</strong> button in the sidebar.</p>
            </div>
          )
        )}
      </div>

      {adding && (
        <AddSpecModal
          onAdd={handleAdd}
          onClose={() => setAdding(false)}
          existingGroups={existingGroups}
          saving={saving}
          error={addError}
        />
      )}

      {showConnect && (
        <ConnectModal
          apiBaseUrl={apiBaseUrl}
          apiKey={apiKey}
          onClose={() => setShowConnect(false)}
        />
      )}
    </div>
  );
}

export default ApiDocs;
